import { Suspense, use, useMemo } from "react";
import { SchemaV4PageRenderer } from "@cogo/react-renderer/adapters/schema-v4-page";
import { loadPageTree } from "../lib/capture-loader";
import { ProvenanceFooter } from "../components/ProvenanceFooter";
import { PRODUCTS } from "../App";

interface ProductDetailProps {
  cartCount: number;
  onNavigate: (path: string) => void;
  onAddToCart: (productId: string) => void;
  selectedProductId: string;
  activeThumbIndex: number;
  setActiveThumbIndex: (index: number) => void;
}

const PRODUCT_IMAGES: Record<string, string[]> = {
  "product-1": [
    "https://images.unsplash.com/photo-1542291026-7eec264c27ff?w=800&auto=format&fit=crop&q=80",
    "https://images.unsplash.com/photo-1608231387042-66d1773070a5?w=800&auto=format&fit=crop&q=80",
    "https://images.unsplash.com/photo-1595950653106-6c9ebd614d3a?w=800&auto=format&fit=crop&q=80",
  ],
  "product-2": [
    "https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=800&auto=format&fit=crop&q=80",
    "https://images.unsplash.com/photo-1547996160-81dfa63595aa?w=800&auto=format&fit=crop&q=80",
    "https://images.unsplash.com/photo-1614162692292-7ac56d7f7f1e?w=800&auto=format&fit=crop&q=80",
  ],
  "product-3": [
    "https://images.unsplash.com/photo-1505740420928-5e560c06d30e?w=800&auto=format&fit=crop&q=80",
    "https://images.unsplash.com/photo-1487215078519-e21cc028cb29?w=800&auto=format&fit=crop&q=80",
    "https://images.unsplash.com/photo-1484704849700-f032a568e944?w=800&auto=format&fit=crop&q=80",
  ],
};

function ProductDetailInner({
  cartCount,
  onNavigate,
  onAddToCart,
  selectedProductId,
  activeThumbIndex,
  setActiveThumbIndex,
}: ProductDetailProps) {
  const page = use(loadPageTree("product-detail"));

  // Find the selected product metadata
  const product = useMemo(() => {
    return PRODUCTS.find((p) => p.id === selectedProductId) || PRODUCTS[2];
  }, [selectedProductId]);

  const images = useMemo(() => {
    return PRODUCT_IMAGES[product.id] || PRODUCT_IMAGES["product-3"];
  }, [product.id]);

  // Deep clone and substitute values in Cogo JSON tree dynamically
  const pageWithState = useMemo(() => {
    const cloned = JSON.parse(JSON.stringify(page));

    // Update navigation cart text if present, or customize other nodes in tree
    const findAndMutate = (node: any) => {
      if (!node) return;
      if (node.id === "brand-tag") {
        node.text = product.brand;
      } else if (node.id === "product-title") {
        node.text = product.name;
      } else if (node.id === "product-price") {
        node.text = product.priceStr;
      } else if (node.id === "product-description") {
        node.text = product.desc;
      } else if (node.id === "main-product-img") {
        node.src = images[activeThumbIndex];
        node.alt = product.name;
      } else if (node.id === "thumb-1") {
        node.src = images[0];
        // add styling to show selected thumbnail
        node.className = `w-20 aspect-square object-cover rounded-lg border cursor-pointer transition ${
          activeThumbIndex === 0 ? "border-indigo-500 opacity-100 ring-2 ring-indigo-500/20" : "border-white/10 opacity-60 hover:opacity-100"
        }`;
      } else if (node.id === "thumb-2") {
        node.src = images[1];
        node.className = `w-20 aspect-square object-cover rounded-lg border cursor-pointer transition ${
          activeThumbIndex === 1 ? "border-indigo-500 opacity-100 ring-2 ring-indigo-500/20" : "border-white/10 opacity-60 hover:opacity-100"
        }`;
      } else if (node.id === "thumb-3") {
        node.src = images[2];
        node.className = `w-20 aspect-square object-cover rounded-lg border cursor-pointer transition ${
          activeThumbIndex === 2 ? "border-indigo-500 opacity-100 ring-2 ring-indigo-500/20" : "border-white/10 opacity-60 hover:opacity-100"
        }`;
      } else if (node.id === "add-cart-btn") {
        node.text = `Add to Cart • ${product.priceStr}`;
      }
      
      if (node.children) {
        node.children.forEach(findAndMutate);
      }
    };

    findAndMutate(cloned.root);
    return cloned;
  }, [page, product, images, activeThumbIndex]);

  const handleEvent = (elementId: string, eventType: string) => {
    if (eventType === "tap") {
      if (elementId === "detail-back-btn" || elementId === "detail-logo") {
        onNavigate("/storefront");
      } else if (elementId === "add-cart-btn") {
        onAddToCart(product.id);
        onNavigate("/checkout");
      } else if (elementId === "thumb-1") {
        setActiveThumbIndex(0);
      } else if (elementId === "thumb-2") {
        setActiveThumbIndex(1);
      } else if (elementId === "thumb-3") {
        setActiveThumbIndex(2);
      }
    }
  };

  return (
    <>
      <SchemaV4PageRenderer
        page={pageWithState}
        dispatchEvent={handleEvent}
        navigate={onNavigate}
        colorScheme="dark"
      />
      <ProvenanceFooter />
    </>
  );
}

export function ProductDetail(props: ProductDetailProps) {
  return (
    <Suspense fallback={<div className="p-8 text-stone-500 bg-slate-950 min-h-screen text-center">Loading Product...</div>}>
      <ProductDetailInner {...props} />
    </Suspense>
  );
}
